/**
 * AttachmentChip — renders a single file attachment as a compact chip.
 * Used both in the input area (with remove button) and in sent messages (compact).
 *
 * Clickability (#639 Task 5.2): a chip is clickable when it has EITHER a
 * `handle` (a session-private CAS binary, viewable through the live-gated
 * `AttachmentViewer` — Task 5.1) or a `path` (a drive reference, opened the
 * way it always has been — `onOpen(path)` → a VFS file tab). Previously only
 * `path` counted, so an inline/binary attachment (no drive path) could never
 * be opened at all — this is the fix for that gap. A handle takes priority
 * over a path when both are somehow present (there is no realistic case
 * where an attachment carries both today).
 *
 * Move-to-drive context menu (#639 Task 6.2): a right-click on a movable
 * chip offers "Move to drive…", opening
 * `SaveToDriveDialog` — the user-triggered twin of the `move_to_drive` LLM
 * tool (renamed from `save_to_drive` — #662 Task 5). A CAS-backed `handle`
 * OR an inline `data:` attachment can be moved this way — the dialog
 * materializes an inline one into the session store first, so it runs the
 * same handle-based path (see `canSaveToDrive` below, and the `data:`
 * decoder in `features/agent/tools/save-to-drive.ts`). The menu entry is
 * absent for a chip that is already moved, carries neither a handle nor
 * inline bytes (a drive `path`), or is still uploading/errored. A successful
 * move is reflected locally as "Moved to …" (#662), and the chip re-points at
 * the resulting drive file.
 */
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { X, File, Image, Loader2, AlertCircle, Cloud, EyeOff } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useTranslation } from '@papan/i18n'
import { aiNoticeMessage } from './attachmentLogic.js'
import type { Attachment } from './hooks.js'
import { AttachmentViewer } from './AttachmentViewer.js'
import { SaveToDriveDialog } from './SaveToDriveDialog.js'

interface AttachmentChipProps {
  attachment: Attachment
  onRemove?: () => void
  onOpen?: (path: string) => void
  compact?: boolean
  /** Session + workspace context (#639 Task 5.2) — needed to build the
   *  session-gated viewer fetch URL for a `handle` attachment. A chip
   *  rendered without these (e.g. a context that hasn't resolved a session
   *  id yet) simply can't open a handle-bearing attachment — clicking it
   *  no-ops rather than throwing. Also gates the Save-to-drive menu entry
   *  (Task 6.2) — the endpoint is session-scoped the same way. */
  sessionId?: string | null
  workspaceId?: string
  /** The message this chip's attachment part belongs to (#662 Task 7) —
   *  threaded through to `SaveToDriveDialog` as `messageId` so the endpoint
   *  can re-point the part. `MessageBubble` (the only renderer today) always
   *  has this in scope. */
  messageId?: string
  /** Fires after a successful move, so a caller can re-point this chip's
   *  attachment locally (fileId + drive path) without a reload (#662 Task 7).
   *  Bubbling this up through `MessageBubble`/`MessageList` is Task 8 — this
   *  prop is unused by any caller yet. */
  onMoved?: (partId: string, drive: { fileId: string; path: string }) => void
}

export function AttachmentChip({ attachment, onRemove, onOpen, compact, sessionId, workspaceId, messageId, onMoved }: AttachmentChipProps) {
  const { t } = useTranslation('agent')
  const [viewerOpen, setViewerOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [movedPath, setMovedPath] = useState<string | null>(null)
  const isImage = attachment.mime.startsWith('image/')
  const showThumbnail = isImage && attachment.url
  // Inline attachments carry a `data:` URL already (every image/text under the
  // inline cap): the viewer renders those with NO network, so they are
  // clickable on their own — this is what makes a pasted screenshot openable
  // and downloadable even though it never became a CAS `handle`. A
  // session-private `handle` opens the fetched viewer; a drive `path` defers
  // to `onOpen`.
  // #662 — a moved attachment points at a drive file: open it in the
  // workspace like any drive file (never the modal), and offer no Move
  // action (it's already a drive file). A moved chip keeps its `path`; a
  // stale `url` left on the part must NOT reopen the private snapshot.
  const isMoved = !!attachment.fileId
  // #662 — the moved-confirmation indicator persists across a browser refresh:
  // prefer the just-moved local value (set right after onSaved, before the
  // parent re-points the part), falling back to the persisted path once the
  // chip is moved (attachment.fileId + attachment.path survive a reload).
  const driveLocation = movedPath ?? (isMoved ? (attachment.path ?? null) : null)
  // #662: a moved chip shows the ACTUAL drive filename (the basename of its
  // drive path), not the original attachment name — a save collision renames
  // to "note123 (1).txt", so the label must match the file the chip opens.
  const displayName = (isMoved && driveLocation)
    ? (driveLocation.split('/').pop() || attachment.filename)
    : attachment.filename
  const clickable = isMoved
    ? !!onOpen && !!attachment.path
    : (!!attachment.url || (!!onOpen && (!!attachment.handle || !!attachment.path)))
  // Save-to-drive works for a CAS handle OR an inline `data:` attachment (a
  // small image / text file whose bytes are in the message part) — the dialog
  // materializes an inline one into the session store first. A drive-path
  // (insert-path) reference has no bytes of its own to save, so it's excluded.
  const isInlineData = !isMoved && !!attachment.url && attachment.url.startsWith('data:')
  const canSaveToDrive = !isMoved && (!!attachment.handle || isInlineData) && !!sessionId && !!workspaceId && attachment.status !== 'uploading'

  const handleClick = () => {
    if (!clickable) return
    // #662 Task 9 — a moved-then-deleted drive file: the chip does NOT probe
    // for existence itself (no bespoke fetch here, per plan). It always calls
    // onOpen(path) and delegates "no longer in the drive" feedback entirely to
    // the host's own path resolution — the same handler markdown path links
    // (`onPathClick`) already share, so a fix belongs there, not per-caller.
    // Verified 2026-09-18: every real onOpen wiring today (WorkspaceView's
    // docked ChatPanel, ChatHomeView/HeroChat, and both mobile chat/workspace
    // screens) resolves the path via `filesApi.resolvePath` and, when it comes
    // back null, surfaces a "File not found" toast (added in this same change
    // set) — so a deleted moved-chip target gives the host's shared feedback,
    // not a per-caller message. The lazy tick-clear (verifyMovedAttachmentByPath)
    // rides that same open. See the "no longer in the drive" delegation test
    // below for what's pinned.
    if (isMoved && attachment.path) { onOpen!(attachment.path); return }
    // Inline bytes render straight from the data URL; a handle is fetched by
    // the viewer (and needs its session/workspace). Either opens the viewer.
    if (attachment.url || (attachment.handle && sessionId && workspaceId)) {
      setViewerOpen(true)
      return
    }
    if (attachment.path) onOpen!(attachment.path)
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!canSaveToDrive) return
    e.preventDefault()
    setMenuPos({ x: e.clientX, y: e.clientY })
  }

  return (
    <>
    <div
      onClick={clickable ? handleClick : undefined}
      onContextMenu={handleContextMenu}
      className={`inline-flex items-center gap-1.5 rounded-md themed-border border themed-bg-tertiary ${
        compact ? 'px-1.5 py-0.5' : 'px-2 py-1'
      } ${attachment.status === 'error' ? 'border-red-500/40' : ''} ${
        clickable ? 'cursor-pointer hover:themed-bg-secondary transition-colors' : ''
      }`}
      title={clickable ? `Open ${displayName}` : undefined}
    >
      {/* Thumbnail or icon */}
      {showThumbnail ? (
        <img
          src={attachment.url}
          alt={displayName}
          className={`rounded object-cover ${compact ? 'w-4 h-4' : 'w-6 h-6'}`}
        />
      ) : (
        isImage ? (
          <Image size={compact ? 10 : 12} className="themed-text-muted shrink-0" />
        ) : (
          <File size={compact ? 10 : 12} className="themed-text-muted shrink-0" />
        )
      )}

      {/* Filename — the drive basename once moved (#662), else the attach name */}
      <span className={`themed-text truncate max-w-[120px] ${compact ? 'text-[10px]' : 'text-xs'}`}>
        {displayName}
      </span>

      {/* Status indicator */}
      {attachment.status === 'uploading' && (
        <Loader2 size={compact ? 8 : 10} className="animate-spin themed-text-muted shrink-0" />
      )}
      {attachment.status === 'error' && (
        <span title={t('attachmentNotices.chipFailed')} className="shrink-0 inline-flex">
          <AlertCircle size={compact ? 8 : 10} className="text-red-400" />
        </span>
      )}
      {/* #1292: attached, but the AI won't see it — the composer spells it out. */}
      {attachment.aiNotice && attachment.status !== 'error' && (
        <span title={aiNoticeMessage(attachment.aiNotice, t)} className="shrink-0 inline-flex">
          <EyeOff size={compact ? 8 : 10} className="text-amber-500" />
        </span>
      )}

      {/* Move-to-drive reflection (#639 Task 6.2, #662) — persists across a
          reload, derived from the moved fileId/path (see `driveLocation`
          above), not just the transient post-move local state. Lucide icons
          don't accept a `title` prop, hence the wrapping span. */}
      {driveLocation && (
        <span title={`In the drive · ${driveLocation}`} className="shrink-0 inline-flex">
          <Cloud size={compact ? 10 : 12} className="text-blue-500" />
        </span>
      )}

      {/* Upload progress bar */}
      {attachment.status === 'uploading' && attachment.progress != null && (
        <div className="w-8 h-1 bg-zinc-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-500 transition-all duration-200"
            style={{ width: `${attachment.progress}%` }}
          />
        </div>
      )}

      {/* Remove button */}
      {onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="themed-text-muted hover:themed-text transition-colors shrink-0"
        >
          <X size={compact ? 8 : 10} />
        </button>
      )}
    </div>
    {viewerOpen && (attachment.url || (attachment.handle && sessionId && workspaceId)) && (
      <AttachmentViewer
        attachment={attachment}
        // Unused for an inline `url` render — the viewer short-circuits before
        // any fetch; required only for the session-gated `handle` fetch path.
        sessionId={sessionId ?? ''}
        workspaceId={workspaceId ?? ''}
        onClose={() => setViewerOpen(false)}
        // A visible, touch-reachable Save-to-drive action (the right-click menu
        // is neither). Only for a handle-bearing attachment — an inline image
        // has no session_attachments row to promote.
        onSaveToDrive={canSaveToDrive ? () => { setViewerOpen(false); setSaveDialogOpen(true) } : undefined}
      />
    )}
    {menuPos && (
      <ChipContextMenu
        x={menuPos.x}
        y={menuPos.y}
        onClose={() => setMenuPos(null)}
        onSaveToDrive={() => { setMenuPos(null); setSaveDialogOpen(true) }}
      />
    )}
    {saveDialogOpen && canSaveToDrive && (
      <SaveToDriveDialog
        filename={attachment.filename}
        handle={attachment.handle}
        url={isInlineData ? attachment.url : undefined}
        mime={attachment.mime}
        sessionId={sessionId!}
        workspaceId={workspaceId!}
        messageId={messageId}
        // #662 Task 8 — only a SENT chip's part id matches a real, persisted
        // message part server-side (repointAttachmentPartsToDrive matches by
        // `messagePartId` alone). A composer (pre-send) chip has no
        // `messageId` — its attachment id was never persisted, so sending it
        // as `messagePartId` would just make the server search 0 matching
        // parts. The composer's own `onMoved` patch (above) is the real
        // mechanism there; omit the futile server re-point call.
        messagePartId={messageId ? attachment.id : undefined}
        onClose={() => setSaveDialogOpen(false)}
        onSaved={(result) => {
          setSaveDialogOpen(false)
          setMovedPath(result.path)
          onMoved?.(attachment.id, { fileId: result.fileId, path: result.path })
          onOpen?.(result.path)
        }}
      />
    )}
    </>
  )
}

/** A minimal single-entry context menu, portalled to `document.body` so it
 *  isn't clipped by a scrolling attachment tray. A backdrop click/right-click
 *  dismisses it — the same "click elsewhere closes" shape every menu in this
 *  app uses, kept local here rather than pulled from a generic menu
 *  component (this chip needs exactly one entry today).
 *
 *  Rendered OUTSIDE `ThemedArea` (a body-level portal), so — per CLAUDE.md's
 *  theming rule — the CSS variables have to be applied by hand on the
 *  content element, never on the backdrop (a theme class sets a solid
 *  background, which on the backdrop would paint over the page instead of
 *  dimming it). */
function ChipContextMenu({
  x, y, onClose, onSaveToDrive,
}: {
  x: number
  y: number
  onClose: () => void
  onSaveToDrive: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200]"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose() }}
      />
      <div
        role="menu"
        className={`${themeClass} fixed z-[201] min-w-[160px] rounded-md themed-border border themed-bg-secondary shadow-lg py-1`}
        style={{ left: x, top: y }}
      >
        <button
          role="menuitem"
          onClick={onSaveToDrive}
          className="w-full text-left px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
        >
          Move to drive…
        </button>
      </div>
    </>,
    document.body,
  )
}
